import React, { useState, useContext, useEffect } from 'react'
import { GlobalContext } from '../context/GlobalContext'
import useApi from '../hooks/useApi'
import clsx from 'clsx'
import moment from 'moment'
import haxios from '../lib/haxios'
import { WrenchScrewdriverIcon, PlusIcon, TrashIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { Loader2 } from 'lucide-react'

const WorkshopEssen = ({ geState, setGEState }) => {
  const { uinfo: operatorData, apir } = useContext(GlobalContext)
  const [showCommentsModal, setShowCommentsModal] = useState(false)
  const [showAddLeadModal, setShowAddLeadModal] = useState(false)
  const [currentComments, setCurrentComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [newImage, setNewImage] = useState('')
  const [selectedLeadId, setSelectedLeadId] = useState(null)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterBranch, setFilterBranch] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [isPosting, setIsPosting] = useState(false)
  console.info('operatorData', operatorData)
  const formatDateToDateTimeLocal = (date) => {
    const ten = (i) => (i < 10 ? '0' : '') + i
    const YYYY = date.getFullYear()
    const MM = ten(date.getMonth() + 1)
    const DD = ten(date.getDate())
    const HH = ten(date.getHours())
    const mm = ten(date.getMinutes())
    return `${YYYY}-${MM}-${DD}T${HH}:${mm}`
  }

  const [newLead, setNewLead] = useState({
    Acct: '',
    Name: '',
    Inits: '',
    Branch: '',
    Tel: '',
    IsPartInGEMag: false,
    GEPartNo: '',
    DateTime: formatDateToDateTimeLocal(new Date()),
    Status: 'Pending',
    Value: ''
  })

  useEffect(() => {
    if (operatorData) {
      setNewLead((prev) => ({
        ...prev,
        Inits: operatorData.Inits,
        Branch: operatorData.Branch
      }))
    }
  }, [operatorData])

  // API calls using useApi hook
  const {
    data: geleads,
    isLoading,
    refetch
  } = useApi({
    url: `/salesassist/cplusassist/geleads${
      operatorData?.Status === 'Sales'
        ? `/${operatorData.Branch}`
        : operatorData?.Status === 'rm'
        ? `?regionalManager=${encodeURIComponent(operatorData.Name)}`
        : ''
    }`,
    initial: [],
    enabled: !!apir && !!operatorData
  })

  const { data: branches } = useApi({
    url: '/salesassist/cplusassist/branches',
    initial: [],
    enabled: !!apir
  })

  const handleAddLead = async () => {
    if (!apir) {
      console.error('APIR not yet loaded')
      return
    }
    try {
      await haxios.post('/salesassist/cplusassist/geleads', newLead)
      await refetch()
      setShowAddLeadModal(false)
      alert('Lead saved successfully!')

      setNewLead({
        Acct: '',
        Name: '',
        Inits: operatorData?.Inits || '',
        Branch: operatorData?.Branch || '',
        Tel: '',
        IsPartInGEMag: false,
        GEPartNo: '',
        DateTime: formatDateToDateTimeLocal(new Date()),
        Status: 'Pending',
        Value: ''
      })
    } catch (error) {
      console.error('Error saving lead:', error)
      alert('An error occurred while saving the lead.')
    }
  }

  const handleAddComment = async () => {
    setIsPosting(true)
    const payload = {
      Comment: newComment,
      User: operatorData?.Inits,
      Image: newImage,
      DateTime: new Date()
    }

    try {
      await haxios.post(`/salesassist/cplusassist/comms/${selectedLeadId}/comments`, payload)
      setCurrentComments((prev) => [...prev, payload])
      setNewComment('')
      setNewImage('')
      await refetch()
    } catch (error) {
      console.error('Error adding comment:', error)
      alert('Failed to add comment')
    } finally {
      setIsPosting(false)
    }
  }

  const handleStatusChange = async (newStatus, id) => {
    try {
      await haxios.post('/salesassist/cplusassist/geleads', { _id: id, Status: newStatus })
      await refetch()
      alert('Status updated successfully')
    } catch (error) {
      console.error('Error updating status:', error)
      alert('Failed to update status')
    }
  }

  const handleDeleteLead = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return

    try {
      await haxios.delete(`/salesassist/cplusassist/geleads/${id}`)
      await refetch()
      alert('Lead deleted successfully')
    } catch (error) {
      console.error('Error deleting lead:', error)
      alert('Failed to delete lead')
    }
  }

  const filteredLeads = geleads.filter((lead) => {
    if (operatorData?.Status === 'sales' && lead.Branch !== operatorData.Branch) return false

    const statusMatch = filterStatus ? lead.Status === filterStatus : true
    const branchMatch = filterBranch ? lead.Branch === filterBranch : true
    const dateMatch =
      fromDate && toDate ? moment(lead.DateTime).isBetween(moment(fromDate), moment(toDate).endOf('day')) : true

    return statusMatch && branchMatch && dateMatch
  })

  // Set document title
  useEffect(() => {
    document.title = 'Workshop Essentials - Sales Assist';
    return () => {
      document.title = 'Sales Assist';
    };
  }, []);

  if (!operatorData) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading user data...</p>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="py-6">
          <div className="px-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading leads...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-orange-100">
              <WrenchScrewdriverIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Workshop Essentials</h1>
              <p className="text-sm text-gray-600 mt-1">Manage leads and track workshop essentials</p>
            </div>
          </div>
          <button
            onClick={() => setShowAddLeadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            New Lead
          </button>
        </div>
      </div>

      <div className="py-6">
        <div className="px-6">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select 
                  value={filterStatus} 
                  onChange={(e) => setFilterStatus(e.target.value)} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Finished">Finished</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
              {operatorData.Status !== 'sales' && (
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                  <select
                    value={filterBranch}
                    onChange={(e) => setFilterBranch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Branches</option>
                    {branches.map((branch, index) => (
                      <option key={index} value={branch.Branch}>
                        {branch.Branch}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Main table */}
          {filteredLeads.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
              <WrenchScrewdriverIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No leads found</h3>
              <p className="text-gray-600 mb-4">Try adjusting your filters or add a new lead.</p>
              <button
                onClick={() => setShowAddLeadModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="w-5 h-5" />
                Add New Lead
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Account</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Initials</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Branch</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Tel</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">GE Part No</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Value</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Comments</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
                      {operatorData.Status === 'admin' && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Delete</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLeads.map((lead) => (
                      <tr 
                        key={lead._id} 
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', {
                              'bg-red-100 text-red-800': lead.Status === 'Pending',
                              'bg-yellow-100 text-yellow-800': lead.Status === 'In Progress',
                              'bg-green-100 text-green-800': lead.Status === 'Finished',
                              'bg-blue-100 text-blue-800': lead.Status === 'Archived'
                            })}
                          >
                            {lead.Status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{lead.Acct || '-'}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{lead.Name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{lead.Inits || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{lead.Branch || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{lead.Tel || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{lead.GEPartNo || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{moment(lead.DateTime).format('DD/MM/YY')}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{lead.Value || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() => {
                              setSelectedLeadId(lead._id)
                              setCurrentComments(lead.Comments || [])
                              setShowCommentsModal(true)
                            }}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            <ChatBubbleLeftRightIcon className="w-4 h-4" />
                            <span className="text-sm font-medium">({lead.Comments?.length || 0})</span>
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <select
                            value={lead.Status}
                            onChange={(e) => handleStatusChange(e.target.value, lead._id)}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          >
                            <option value="Pending">Pending</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Finished">Finished</option>
                            <option value="Archived" disabled={operatorData.Status === 'sales'}>
                              Archived
                            </option>
                          </select>
                        </td>
                        {operatorData.Status === 'admin' && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button 
                              onClick={() => handleDeleteLead(lead._id)} 
                              className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

      {/* Comments Modal */}
      {showCommentsModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Comments</h2>
              <button 
                onClick={() => setShowCommentsModal(false)} 
                className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="overflow-y-auto p-6 max-h-96">
              {currentComments.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No comments yet. Be the first to comment!</p>
              ) : (
                currentComments.map((comment, index) => (
                  <div
                    key={index}
                    className={clsx(
                      'mb-4 p-4 rounded-lg shadow-sm',
                      comment.User === operatorData.Inits ? 'ml-auto bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200',
                      'max-w-[70%]'
                    )}
                  >
                    <div className="flex gap-2 items-center mb-2">
                      <span className="font-medium text-gray-900">{comment.User}</span>
                      <span className="text-sm text-gray-500">{moment(comment.DateTime).format('DD/MM/YY HH:mm')}</span>
                    </div>
                    {comment.Comment && <p className="text-gray-700 mb-2">{comment.Comment}</p>}
                    {comment.Image && (
                      <img src={comment.Image} alt="Comment attachment" className="max-w-full rounded-lg mt-2" />
                    )}
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-4 items-end p-6 border-t border-gray-200">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows={3}
              />
              <button
                onClick={handleAddComment}
                disabled={isPosting || !newComment.trim()}
                className={clsx(
                  'px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors font-medium',
                  (isPosting || !newComment.trim()) && 'opacity-50 cursor-not-allowed'
                )}
              >
                {isPosting ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Add New Lead</h2>
              <button 
                onClick={() => setShowAddLeadModal(false)} 
                className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account</label>
                  <input
                    type="text"
                    value={newLead.Acct}
                    onChange={(e) => setNewLead({ ...newLead, Acct: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={newLead.Name}
                    onChange={(e) => setNewLead({ ...newLead, Name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telephone</label>
                  <input
                    type="text"
                    value={newLead.Tel}
                    onChange={(e) => setNewLead({ ...newLead, Tel: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GE Part Number</label>
                  <input
                    type="text"
                    value={newLead.GEPartNo}
                    onChange={(e) => setNewLead({ ...newLead, GEPartNo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                  <input
                    type="text"
                    value={newLead.Value}
                    onChange={(e) => setNewLead({ ...newLead, Value: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date/Time</label>
                  <input
                    type="datetime-local"
                    value={newLead.DateTime}
                    onChange={(e) => setNewLead({ ...newLead, DateTime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="flex gap-2 items-center">
                    <input
                      type="checkbox"
                      checked={newLead.IsPartInGEMag}
                      onChange={(e) => setNewLead({ ...newLead, IsPartInGEMag: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Part in GE Magazine</span>
                  </label>
                </div>

                <div className="col-span-2 flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button 
                    onClick={() => setShowAddLeadModal(false)} 
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleAddLead} 
                    className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Save Lead
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  )
}

export default WorkshopEssen
